/**
 * Google Analytics 4 (gtag) — marketing / auth funnel plus the admin dashboard landing.
 * Measurement ID from client: G-MYX8CFRT1N
 *
 * Staff/partner dashboards and admin sub-pages stay untracked.
 */

export const GA_MEASUREMENT_ID = 'G-MYX8CFRT1N';

/** Paths where GA may load and send page_view / campaign events */
const PUBLIC_PATH_EXACT = new Set([
  '/',
  '/admin',
  '/admin/signup',
  '/admin/dashboard',
  '/login',
  '/verify-email',
  '/terms',
  '/privacy',
  '/partner',
]);

export function isAnalyticsPublicPath(pathname = '') {
  if (PUBLIC_PATH_EXACT.has(pathname)) return true;
  // Allow query strings on signup (e.g. ?ref=)
  if (pathname.startsWith('/admin/signup')) return true;
  return false;
}

let scriptLoading = false;
let scriptLoaded = false;

function injectGtagScript() {
  if (typeof window === 'undefined') return;
  if (scriptLoaded || scriptLoading) return;

  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
  }

  // Queued before gtag.js loads so the first page_view is never processed
  // ahead of its destination config.
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false, // we send page_view manually for SPA public routes only
    page_location: window.location.href,
    page_title: document.title,
  });

  // Google Ads gtag.js may already be in index.html (AW-18441034628).
  // Reuse that loader instead of injecting a second copy.
  const alreadyHasGtagJs = !!document.querySelector(
    'script[src*="googletagmanager.com/gtag/js"]'
  );
  if (alreadyHasGtagJs) {
    scriptLoaded = true;
    return;
  }

  scriptLoading = true;
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script.onload = () => {
    scriptLoaded = true;
    scriptLoading = false;
  };
  script.onerror = () => {
    scriptLoading = false;
  };
  document.head.appendChild(script);
}

/** Ensure gtag is loaded only on public marketing/auth routes */
export function ensureAnalytics(pathname) {
  if (!isAnalyticsPublicPath(pathname)) return false;
  injectGtagScript();
  return true;
}

export function trackPageView(pathname, title) {
  if (!isAnalyticsPublicPath(pathname)) return;
  injectGtagScript();
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

  const pageLocation = window.location.href;
  const pageTitle = title || document.title;

  // `set` applies the URL to every linked destination (GA4 plus any linked
  // Google Ads account); event-level params alone don't reach them.
  window.gtag('set', {
    page_location: pageLocation,
    page_path: pathname,
    page_title: pageTitle,
    page_referrer: document.referrer || undefined,
  });

  window.gtag('event', 'page_view', {
    page_path: pathname,
    page_title: pageTitle,
    page_location: pageLocation,
  });
}

/**
 * Push a GTM Custom Event onto dataLayer (plain object form).
 * GTM Custom Event triggers listen for `{ event: '…' }` — not gtag Arguments.
 *
 * IMPORTANT: Do not put extra GA fields here for signup_success — marketing
 * listens for the exact shape { event: 'signup_success', method }.
 */
export function pushDataLayerEvent(eventName, params = {}) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ...params,
  });
}

/**
 * Confirmed new-account creation only — fires exactly once per page load.
 *
 * GTM listens for this plain Custom Event (not gtag Arguments).
 * Do NOT also call gtag('event', 'signup_success') — that double-fires.
 *
 * method: 'email' | 'google'
 * Does NOT fire on regular logins, refresh, or dashboard return.
 */
let signupSuccessFired = false;

export function trackSignupSuccess(method = 'email') {
  if (typeof window === 'undefined') return;
  if (signupSuccessFired) return;
  signupSuccessFired = true;

  const normalized =
    method === 'google' || method === 'admin_google' ? 'google' : 'email';

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'signup_success',
    method: normalized,
  });
}

/**
 * Fire a GA4 event. Silently no-ops on non-public paths or if gtag missing.
 * @param {string} eventName
 * @param {Record<string, string|number|boolean>} [params]
 */
export function trackEvent(eventName, params = {}) {
  if (typeof window === 'undefined') return;
  const path = window.location?.pathname || '';
  if (!isAnalyticsPublicPath(path)) return;

  injectGtagScript();
  if (typeof window.gtag !== 'function') return;

  window.gtag('event', eventName, {
    ...params,
    page_path: path,
  });
}

/** Named campaign helpers */
export const analytics = {
  getStarted: (location = 'unknown') =>
    trackEvent('get_started_click', { event_category: 'cta', location }),
  signupStart: () =>
    trackEvent('signup_start', { event_category: 'signup' }),
  signupSubmit: () =>
    trackEvent('signup_submit', { event_category: 'signup' }),
  /** @see trackSignupSuccess — new accounts only */
  signupSuccess: (method = 'email') => trackSignupSuccess(method),
  formStart: (formName) =>
    trackEvent('form_start', { event_category: 'form', form_name: formName }),
  formSubmit: (formName) =>
    trackEvent('form_submit', { event_category: 'form', form_name: formName }),
  loginClick: (role = 'admin') =>
    trackEvent('login_click', { event_category: 'auth', role }),
  loginSubmit: (role = 'admin') =>
    trackEvent('login_submit', { event_category: 'auth', role }),
  /** Existing-user auth only — never use for new signups */
  loginSuccess: (role = 'admin') =>
    trackEvent('login_success', { event_category: 'auth', role }),
  downloadClick: (platform = 'unknown') =>
    trackEvent('download_click', { event_category: 'download', platform }),
  contactClick: () =>
    trackEvent('contact_click', { event_category: 'engagement' }),
};

export default analytics;
